import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, Typography } from '@material-ui/core';
import { ListRowProps } from 'react-virtualized';
import { useLoadMoreNearEnd } from 'components/Table/useLoadMoreNearEnd';
import { useIsMobile } from 'components/Page/useIsMobile';
import { VirtualizedList } from 'components/Table/VirtualizedList';
import { ICharacterMapFeed } from '@pvplevelup/core/dist/queries/character/character';
import { ZoneInformation } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import { MapThumbnailIcons } from 'utilities/map_icons';
import { VirtualizedTable } from 'components/Table/VirtualizedTable';
import { usePageStyles } from 'components/Page/PageKit';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins

const TEAM_COLORS = ['rgb(62, 124, 255)', 'rgb(228, 54, 55)'];

const useStyles = makeStyles((theme) => ({
  mapCell: {
    display: 'grid',
    gridTemplateColumns: '96px minmax(0, 1fr)',
    gridGap: theme.spacing(1.5),
    alignItems: 'center',
    width: '100%',
  },
  thumb: {
    width: 96,
    height: 54,
    objectFit: 'cover',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.05)',
  },
  name: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  muted: {
    fontSize: '0.8rem',
    opacity: 0.55,
  },
  barCell: {
    width: '100%',
    paddingRight: theme.spacing(2),
  },
  barValue: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    alignItems: 'baseline',
    gridGap: theme.spacing(1),
  },
  big: {
    fontWeight: 700,
    fontSize: '1.05rem',
  },
  track: {
    marginTop: 6,
    height: 6,
    borderRadius: 3,
    background: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 3,
  },
  // phone layout: one card per map
  mCard: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  mTop: {
    display: 'grid',
    gridTemplateColumns: '72px minmax(0, 1fr) auto',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
  },
  mSides: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gridGap: theme.spacing(1.5),
    marginTop: theme.spacing(1.25),
  },
}));

/** green over 55%, red under 45% */
const rateColor = (rate: number) => rate >= 0.55 ? '#43a047' : rate <= 0.45 ? '#e53935' : '#fb8c00';

const Bar: React.FC<{value: number, color: string}> = ({value, color}) => {
  const classes = useStyles();
  return (
    <div className={classes.track}>
      <div className={classes.fill} style={{width: `${Math.max(0, Math.min(100, value * 100))}%`, background: color}} />
    </div>
  );
}

interface Props {
  /** a character's or a user's map feed (same shape) */
  mapFeed: ICharacterMapFeed | {maps?: any[]} | undefined;
  fetchMoreRecords: () => void;
  /** fills the tab by default */
  height?: string;
}

/** The phone version of a map row: the map, its win rate, then games and the win rate from each side. */
const MobileMapRow: React.FC<{map: any, measure: () => void}> = ({map, measure}) => {
  const classes = useStyles();
  useEffect(() => { measure(); }, []);
  const zone = ZoneInformation[map.zone || ""] || null;
  const currentMap = zone ? zone.mapInfo[map.map || ""] : null;
  const image = MapThumbnailIcons[map.zone || ""]?.[map.map || ""] || null;
  const blueGames = (map.blue_wins || 0) + (map.blue_loses || 0);
  const redGames = (map.red_wins || 0) + (map.red_loses || 0);
  const wr = map.wr || 0;
  const side = (wins: number, games: number, color: string, label: string) => (
    <div>
      <div className={classes.big} style={{fontSize: '0.95rem'}}>{games > 0 ? `${Math.round(wins / games * 100)}%` : '-'}</div>
      <Bar value={games > 0 ? wins / games : 0} color={color} />
      <div className={classes.muted} style={{marginTop: 4}}>{label}</div>
    </div>
  );
  return (
    <div style={{paddingBottom: 1}}>
      <div className={classes.mCard}>
        <div className={classes.mTop}>
          {image ? <img className={classes.thumb} style={{width: 72, height: 42}} src={image} /> : <div className={classes.thumb} style={{width: 72, height: 42}} />}
          <div style={{minWidth: 0}}>
            <div className={classes.name}>{currentMap?.name || 'Unknown map'}</div>
            <div className={classes.muted}>{zone?.name}</div>
          </div>
          <div style={{textAlign: 'right'}}>
            <div className={classes.big} style={{color: rateColor(wr)}}>{Math.round(wr * 100)}%</div>
            <div className={classes.muted}>{map.wins || 0}W {map.loses || 0}L</div>
          </div>
        </div>
        <Bar value={wr} color={rateColor(wr)} />
        <div className={classes.mSides}>
          <div>
            <div className={classes.big} style={{fontSize: '0.95rem'}}>{map.played || 0}</div>
            <div className={classes.muted} style={{marginTop: 4}}>Games</div>
          </div>
          {side(map.blue_wins || 0, blueGames, TEAM_COLORS[0], 'Blue side')}
          {side(map.red_wins || 0, redGames, TEAM_COLORS[1], 'Red side')}
        </div>
      </div>
    </div>
  );
}

/** The Maps tab of the character and user pages: how they do on each map, and from which side. */
export const ProfileMaps: React.FC<Props> = ({mapFeed, fetchMoreRecords, height = '100%'}) => {
  const classes = useStyles();
  const page = usePageStyles();
  const mobile = useIsMobile();
  const maps = mapFeed?.maps || [];
  const mostPlayed = maps.reduce((max, m) => Math.max(max, m.played || 0), 0) || 1;
  const onRowsRendered = useLoadMoreNearEnd(maps.length, (mapFeed as any)?.hasNext, fetchMoreRecords, maps[0] && `${maps[0].zone}-${maps[0].map}`);

  if(mobile)
    return (
      <div style={{height, position: 'relative'}}>
        <VirtualizedList
          data={maps}
          overscanRowCount={6}
          onRowsRendered={onRowsRendered}
          customRenderer={(props: ListRowProps, map: any, measure: () => void) => <MobileMapRow map={map} measure={measure} />}
        />
        {mapFeed && maps.length == 0 &&
          <div className={page.empty} style={{position: 'absolute', top: 0, left: 0, right: 0}}>
            <Typography>No maps played with these filters.</Typography>
          </div>
        }
      </div>
    );

  return (
    <div className={page.tablePanel} style={{height}}>
      <VirtualizedTable
        minWidth={820}
        rowHeight={72}
        headerHeight={40}
        rowCount={maps.length}
        rowGetter={({ index }) => maps[index]}
        onRowsRendered={onRowsRendered}
        customCellRenderer={({columnIndex, rowIndex, rowData}) => {
          const map = rowData;
          const zone = ZoneInformation[map.zone || ""] || null;
          const currentMap = zone ? zone.mapInfo[map.map || ""] : null;
          const image = MapThumbnailIcons[map.zone || ""]?.[map.map || ""] || null;
          const blueGames = (map.blue_wins || 0) + (map.blue_loses || 0);
          const redGames = (map.red_wins || 0) + (map.red_loses || 0);
          const bwr = blueGames > 0 ? (map.blue_wins || 0) / blueGames : 0;
          const rwr = redGames > 0 ? (map.red_wins || 0) / redGames : 0;
          const wr = map.wr || 0;
          return (<React.Fragment>
            {columnIndex == 0 ?
              <div className={classes.mapCell}>
                {image ? <img className={classes.thumb} src={image} /> : <div className={classes.thumb} />}
                <div style={{minWidth: 0}}>
                  <div className={classes.name}>{currentMap?.name || 'Unknown map'}</div>
                  <div className={classes.muted}>{zone?.name}</div>
                </div>
              </div>
            : columnIndex == 1 ?
              <div className={classes.barCell}>
                <div className={classes.barValue}>
                  <span className={classes.big} style={{color: rateColor(wr)}}>{Math.round(wr * 100)}%</span>
                  <span className={classes.muted}>{map.wins || 0}W {map.loses || 0}L</span>
                </div>
                <Bar value={wr} color={rateColor(wr)} />
              </div>
            : columnIndex == 2 ?
              <div className={classes.barCell}>
                <span className={classes.big}>{map.played || 0}</span>
                <Bar value={(map.played || 0) / mostPlayed} color="rgba(255,255,255,0.45)" />
              </div>
            : columnIndex == 3 ?
              <Tooltip title={`${map.blue_wins || 0} wins in ${blueGames} games on the blue side`}>
                <div className={classes.barCell}>
                  <span className={classes.big}>{blueGames > 0 ? `${Math.round(bwr * 100)}%` : '-'}</span>
                  <Bar value={bwr} color={TEAM_COLORS[0]} />
                </div>
              </Tooltip>
            : columnIndex == 4 ?
              <Tooltip title={`${map.red_wins || 0} wins in ${redGames} games on the red side`}>
                <div className={classes.barCell}>
                  <span className={classes.big}>{redGames > 0 ? `${Math.round(rwr * 100)}%` : '-'}</span>
                  <Bar value={rwr} color={TEAM_COLORS[1]} />
                </div>
              </Tooltip>
            : null
            }
          </React.Fragment>)
        }}
        columns={[
          {width: 280, label: 'Map', dataKey: 'map'},
          {width: 170, label: 'Win Rate', dataKey: 'wr'},
          {width: 150, label: 'Games', dataKey: 'played'},
          {width: 150, label: 'Blue Side Win Rate', dataKey: 'blue_wins'},
          {width: 150, label: 'Red Side Win Rate', dataKey: 'red_wins'},
        ]}
      />
      {mapFeed && maps.length == 0 &&
        <div className={page.empty} style={{position: 'absolute', top: 40, left: 0, right: 0}}>
          <Typography>No maps played with these filters.</Typography>
        </div>
      }
    </div>
  );
}
